import React, { useState, useEffect, useRef } from "react";
import { database } from "../firebaseConfig";
import { ref, update, onValue, push, set, remove } from "firebase/database";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./AgentsSettings.css";

const AgentsSettings = ({ userId }) => {
  const [agents, setAgents] = useState([]);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [newAgent, setNewAgent] = useState({
    name: '',
    case: '',
    task: '',
    type: 'Reply',
    happinessMin: 1,
    happinessMax: 10,
    useCalendly: false,
    selectedEventType: null,
    calendlyGuidelines: ''
  });
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsMessage, setAgentsMessage] = useState('');
  const [infoCollapsed, setInfoCollapsed] = useState(true);

  // Calendly Event Types States
  const [availableEventTypes, setAvailableEventTypes] = useState([]);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);
  const [eventTypesError, setEventTypesError] = useState("");

  const functions = getFunctions();
  const updateAgentCaseNumbers = httpsCallable(functions, 'updateAgentCaseNumbers');
  const processEmailWithAgents = httpsCallable(functions, 'processEmailWithAgents');
  const getCalendlyEventTypes = httpsCallable(functions, 'getCalendlyEventTypes');

  // Lade Agents aus der Datenbank
  useEffect(() => {
    if (!userId) return;
    
    const agentsRef = ref(database, `/World/${userId}/settings/agents`);
    const unsubscribe = onValue(agentsRef, (snapshot) => {
      if (snapshot.exists()) {
        const agentsData = [];
        snapshot.forEach((childSnapshot) => {
          const agentData = {
            id: childSnapshot.key,
            ...childSnapshot.val()
          };
          
          agentsData.push(agentData);
        });
        
        // Sortiere Agents nach Case-Nummer
        agentsData.sort((a, b) => (a.caseNumber || 0) - (b.caseNumber || 0));
        setAgents(agentsData);
        
        console.log('🔧 [AGENT-LOAD] Loaded', agentsData.length, 'agents from database');
        agentsData.forEach(agent => {
          if (agent.useCalendly) {
            console.log('🔧 [AGENT-LOAD] Agent with Calendly:', {
              name: agent.name,
              useCalendly: agent.useCalendly,
              selectedEventType: agent.selectedEventType,
              selectedEventTypeType: typeof agent.selectedEventType
            });
          }
        });
      } else {
        setAgents([]);
      }
      setAgentsLoading(false);
    });
    
    return () => unsubscribe();
  }, [userId]);

  // Aktualisiere Case-Nummern beim ersten Laden
  useEffect(() => {
    if (agents.length > 0 && !agents[0].caseNumber) {
      handleUpdateCaseNumbers();
    }
  }, [agents]);

  // Überwache Änderungen am editingAgent.selectedEventType
  useEffect(() => {
    if (editingAgent) {
      console.log('🔧 [EDITING-AGENT-CHANGE] editingAgent.selectedEventType changed:', {
        selectedEventType: editingAgent.selectedEventType,
        selectedEventTypeType: typeof editingAgent.selectedEventType,
        useCalendly: editingAgent.useCalendly,
        agentName: editingAgent.name
      });
    }
  }, [editingAgent?.selectedEventType]);

  // Überwache Änderungen am newAgent.selectedEventType
  useEffect(() => {
    console.log('🔧 [NEW-AGENT-CHANGE] newAgent.selectedEventType changed:', {
      selectedEventType: newAgent.selectedEventType,
      selectedEventTypeType: typeof newAgent.selectedEventType,
      useCalendly: newAgent.useCalendly
    });
  }, [newAgent.selectedEventType]);

  // Happiness Range Slider Component
  const HappinessRangeSlider = ({ minValue, maxValue, onMinChange, onMaxChange, disabled = false }) => {
    const [isDragging, setIsDragging] = useState(null); // 'min', 'max', or null
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartValue, setDragStartValue] = useState(0);
    const trackRef = useRef(null);

    const getValueFromPosition = (clientX, trackElement) => {
      const rect = trackElement.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return Math.round(1 + percentage * 9);
    };

    const handleMouseDown = (e, type) => {
      if (disabled) return;
      e.preventDefault();
      setIsDragging(type);
      setDragStartX(e.clientX);
      setDragStartValue(type === 'min' ? minValue : maxValue);
    };

    const handleMouseMove = (e) => {
      if (!isDragging || disabled || !trackRef.current) return;
      e.preventDefault();
      
      const newValue = getValueFromPosition(e.clientX, trackRef.current);
      
      if (isDragging === 'min' && newValue <= maxValue) {
        onMinChange(newValue);
      } else if (isDragging === 'max' && newValue >= minValue) {
        onMaxChange(newValue);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(null);
    };

    const handleTrackClick = (e) => {
      if (disabled || isDragging) return;
      
      const newValue = getValueFromPosition(e.clientX, e.currentTarget);
      const distanceToMin = Math.abs(newValue - minValue);
      const distanceToMax = Math.abs(newValue - maxValue);
      
      if (distanceToMin < distanceToMax) {
        if (newValue <= maxValue) onMinChange(newValue);
      } else {
        if (newValue >= minValue) onMaxChange(newValue);
      }
    };

    // Event listeners für globale Mouse-Events
    useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
    }, [isDragging, minValue, maxValue]);

    const minPosition = ((minValue - 1) / 9) * 100;
    const maxPosition = ((maxValue - 1) / 9) * 100;
    const rangeWidth = maxPosition - minPosition;

    return (
      <div className="happiness-range-container">
        <div className="happiness-range-header">
          <div className="happiness-icon">😊</div>
          <div className="happiness-label">Happiness Range</div>
          <div className="happiness-values">{minValue} - {maxValue}</div>
        </div>
        
        <div className="happiness-slider-container">
          <div 
            ref={trackRef}
            className="happiness-track" 
            onClick={handleTrackClick}
          >
            <div 
              className="happiness-range-fill"
              style={{
                left: `${minPosition}%`,
                width: `${rangeWidth}%`
              }}
            />
            
            <div 
              className={`happiness-thumb happiness-thumb-min ${isDragging === 'min' ? 'dragging' : ''}`}
              style={{ left: `${minPosition}%` }}
              onMouseDown={(e) => handleMouseDown(e, 'min')}
            >
              <div className="happiness-thumb-value">{minValue}</div>
            </div>
            
            <div 
              className={`happiness-thumb happiness-thumb-max ${isDragging === 'max' ? 'dragging' : ''}`}
              style={{ left: `${maxPosition}%` }}
              onMouseDown={(e) => handleMouseDown(e, 'max')}
            >
              <div className="happiness-thumb-value">{maxValue}</div>
            </div>
          </div>
        </div>
        
        <div className="happiness-labels">
          <span>😢 1</span>
          <span>😐 5</span>
          <span>😄 10</span>
        </div>
        
        <div className="happiness-description">
          Agent will work with contacts having happiness level between {minValue} and {maxValue}
        </div>
      </div>
    );
  };

  // Funktion zum Aktualisieren der Case-Nummern
  const handleUpdateCaseNumbers = async () => {
    try {
      setAgentsMessage('Updating case numbers...');
      const result = await updateAgentCaseNumbers();
      console.log('Case numbers updated:', result.data);
      setAgentsMessage('Case numbers updated successfully.');
      setTimeout(() => setAgentsMessage(''), 3000);
    } catch (error) {
      console.error('Error updating case numbers:', error);
      setAgentsMessage(`Error updating case numbers: ${error.message}`);
    }
  };

  // Funktion zum Testen der E-Mail-Verarbeitung
  const handleTestEmailProcessing = async (emailId) => {
    try {
      setAgentsMessage('Processing email with agents...');
      const result = await processEmailWithAgents({ emailId, isSpam: false });
      console.log('Email processing result:', result.data);
      setAgentsMessage(`Email processed successfully! Draft created by ${result.data.agentUsed.name} (Case ${result.data.agentUsed.caseNumber})`);
      setTimeout(() => setAgentsMessage(''), 5000);
    } catch (error) {
      console.error('Error processing email:', error);
      setAgentsMessage(`Error processing email: ${error.message}`);
    }
  };

  // Funktion zum Hinzufügen eines neuen Agents
  const handleAddAgent = async () => {
    // Validierung basierend auf Agent-Type
    if (!newAgent.name.trim() || !newAgent.case.trim()) {
      setAgentsMessage('Please fill in name and case description.');
      return;
    }
    
    if (newAgent.type === 'Reply' && !newAgent.task.trim()) {
      setAgentsMessage('Please fill in task instructions for Reply agents.');
      return;
    }
    
    try {
      setAgentsMessage('Saving agent...');
      
      // Debug logging für newAgent
      console.log('🔧 [AGENT-ADD] newAgent state before saving:', {
        name: newAgent.name,
        useCalendly: newAgent.useCalendly,
        selectedEventType: newAgent.selectedEventType,
        selectedEventTypeType: typeof newAgent.selectedEventType,
        selectedEventTypeLength: newAgent.selectedEventType ? newAgent.selectedEventType.length : 'N/A'
      });
      
      const agentsRef = ref(database, `/World/${userId}/settings/agents`);
      const newAgentRef = push(agentsRef);
      
      // Bestimme die nächste Case-Nummer
      const nextCaseNumber = agents.length + 1;
      
      const agentData = {
        name: newAgent.name.trim(),
        case: newAgent.case.trim(),
        type: newAgent.type,
        happinessMin: newAgent.happinessMin,
        happinessMax: newAgent.happinessMax,
        caseNumber: nextCaseNumber,
        createdAt: new Date().toISOString(),
        useCalendly: newAgent.useCalendly || false,
        selectedEventType: newAgent.selectedEventType || null,
        calendlyGuidelines: newAgent.calendlyGuidelines || ''
      };
      
      // Task nur bei Reply-Agents hinzufügen
      if (newAgent.type === 'Reply') {
        agentData.task = newAgent.task.trim();
      }
      
      // Debug logging für agentData
      console.log('🔧 [AGENT-ADD] Final agentData to be sent to Firebase:', {
        ...agentData,
        selectedEventType: agentData.selectedEventType,
        selectedEventTypeType: typeof agentData.selectedEventType,
        useCalendly: agentData.useCalendly
      });
      
      await set(newAgentRef, agentData);
      
      console.log('🔧 [AGENT-ADD] Agent successfully saved to Firebase');
      
      setAgentsMessage('Agent successfully added.');
      setShowAddAgent(false);
      setNewAgent({ name: '', case: '', task: '', type: 'Reply', happinessMin: 1, happinessMax: 10, useCalendly: false, selectedEventType: null, calendlyGuidelines: '' });
      setTimeout(() => setAgentsMessage(''), 3000);
      
      // Aktualisiere alle Case-Nummern nach dem Hinzufügen
      setTimeout(() => handleUpdateCaseNumbers(), 1000);
    } catch (error) {
      console.error('Error adding agent:', error);
      setAgentsMessage(`Error: ${error.message}`);
    }
  };

  // Funktion zum Aktualisieren eines Agents
  const handleUpdateAgent = async () => {
    // Validierung basierend auf Agent-Type
    if (!editingAgent || !editingAgent.name.trim() || !editingAgent.case.trim()) {
      setAgentsMessage('Please fill in name and case description.');
      return;
    }
    
    if (editingAgent.type === 'Reply' && !editingAgent.task.trim()) {
      setAgentsMessage('Please fill in task instructions for Reply agents.');
      return;
    }
    
    try {
      setAgentsMessage('Updating agent...');
      
      // Debug logging für editingAgent
      console.log('🔧 [AGENT-UPDATE] editingAgent state before saving:', {
        name: editingAgent.name,
        useCalendly: editingAgent.useCalendly,
        selectedEventType: editingAgent.selectedEventType,
        selectedEventTypeType: typeof editingAgent.selectedEventType,
        selectedEventTypeLength: editingAgent.selectedEventType ? editingAgent.selectedEventType.length : 'N/A'
      });
      
      const agentRef = ref(database, `/World/${userId}/settings/agents/${editingAgent.id}`);
      
      const updateData = {
        name: editingAgent.name.trim(),
        case: editingAgent.case.trim(),
        type: editingAgent.type,
        happinessMin: editingAgent.happinessMin || 1,
        happinessMax: editingAgent.happinessMax || 10,
        updatedAt: new Date().toISOString(),
        useCalendly: editingAgent.useCalendly || false,
        selectedEventType: editingAgent.selectedEventType || null,
        calendlyGuidelines: editingAgent.calendlyGuidelines || ''
      };
      
      // Task nur bei Reply-Agents hinzufügen/aktualisieren
      if (editingAgent.type === 'Reply') {
        updateData.task = editingAgent.task.trim();
      } else {
        // Bei No Reply Agents das Task-Feld entfernen, falls es existiert
        updateData.task = null;
      }
      
      // Debug logging für updateData
      console.log('🔧 [AGENT-UPDATE] Final updateData to be sent to Firebase:', {
        ...updateData,
        selectedEventType: updateData.selectedEventType,
        selectedEventTypeType: typeof updateData.selectedEventType,
        useCalendly: updateData.useCalendly
      });
      
      await update(agentRef, updateData);
      
      console.log('🔧 [AGENT-UPDATE] Agent successfully updated in Firebase');
      
      setAgentsMessage('Agent successfully updated.');
      setEditingAgent(null);
      setTimeout(() => setAgentsMessage(''), 3000);
    } catch (error) {
      console.error('Error updating agent:', error);
      setAgentsMessage(`Error: ${error.message}`);
    }
  };

  // Funktion zum Löschen eines Agents
  const handleDeleteAgent = async (agent) => {
    if (!agent || !window.confirm(`Are you sure you want to delete the agent "${agent.name}"?`)) {
      return;
    }
    
    try {
      setAgentsMessage('Deleting agent...');
      
      const agentRef = ref(database, `/World/${userId}/settings/agents/${agent.id}`);
      await remove(agentRef);
      
      setAgentsMessage('Agent successfully deleted.');
      setTimeout(() => setAgentsMessage(''), 3000);
      
      // Aktualisiere Case-Nummern nach dem Löschen
      setTimeout(() => handleUpdateCaseNumbers(), 1000);
    } catch (error) {
      console.error('Error deleting agent:', error);
      setAgentsMessage(`Error: ${error.message}`);
    }
  };

  // Funktion zum Bearbeiten eines Agents
  const handleEditAgent = (agent) => {
    setEditingAgent({ 
      ...agent,
      happinessMin: agent.happinessMin || 1,
      happinessMax: agent.happinessMax || 10,
      useCalendly: agent.useCalendly || false,
      selectedEventType: agent.selectedEventType || null,
      calendlyGuidelines: agent.calendlyGuidelines || ''
    });
    setShowAddAgent(false);
  };

  // Funktion zum Abbrechen der Bearbeitung
  const handleCancelEdit = () => {
    setEditingAgent(null);
    setAgentsMessage('');
  };

  // Funktion zum Abbrechen des Hinzufügens
  const handleCancelAdd = () => {
    setShowAddAgent(false);
    setNewAgent({ name: '', case: '', task: '', type: 'Reply', happinessMin: 1, happinessMax: 10, useCalendly: false, selectedEventType: null, calendlyGuidelines: '' });
    setAgentsMessage('');
  };

  // Funktion zum Laden der verfügbaren Calendly Event Types
  const handleLoadEventTypes = async () => {
    console.log('🔧 [LOAD-EVENT-TYPES] Starting to load event types...');
    
    setLoadingEventTypes(true);
    setEventTypesError("");

    try {
      console.log('🔧 [LOAD-EVENT-TYPES] Loading Calendly key from moreSettings...');
      
      // Lade Calendly Key aus moreSettings
      const moreSettingsRef = ref(database, `/World/${userId}/settings/moreSettings`);
      const moreSettingsSnapshot = await new Promise((resolve, reject) => {
        onValue(moreSettingsRef, resolve, reject, { onlyOnce: true });
      });

      if (!moreSettingsSnapshot.exists()) {
        console.log('🔧 [LOAD-EVENT-TYPES] No moreSettings found');
        setEventTypesError("No Calendly settings found. Please configure your Calendly API key in Extra Settings first.");
        return;
      }

      const moreSettings = moreSettingsSnapshot.val();
      const calendlyKey = moreSettings.calendlyKey;

      console.log('🔧 [LOAD-EVENT-TYPES] Found moreSettings:', {
        hasCalendlyKey: !!calendlyKey,
        calendlyKeyLength: calendlyKey ? calendlyKey.length : 'N/A'
      });

      if (!calendlyKey) {
        console.log('🔧 [LOAD-EVENT-TYPES] No Calendly key found');
        setEventTypesError("No Calendly API key found. Please configure it in Extra Settings first.");
        return;
      }

      console.log('🔧 [LOAD-EVENT-TYPES] Calling getCalendlyEventTypes function...');
      const result = await getCalendlyEventTypes({ calendlyKey });
      
      console.log('🔧 [LOAD-EVENT-TYPES] getCalendlyEventTypes result:', {
        success: result.data.success,
        eventTypesCount: result.data.eventTypes ? result.data.eventTypes.length : 0,
        error: result.data.error || 'none'
      });
      
      if (result.data.success) {
        console.log('🔧 [LOAD-EVENT-TYPES] Setting available event types:', result.data.eventTypes);
        setAvailableEventTypes(result.data.eventTypes);
        setAgentsMessage(`Found ${result.data.eventTypes.length} event types available for selection.`);
        setTimeout(() => setAgentsMessage(''), 3000);
      } else {
        console.log('🔧 [LOAD-EVENT-TYPES] Failed to load event types:', result.data.error);
        setEventTypesError(result.data.error || "Failed to load event types.");
      }
    } catch (error) {
      console.error("🔧 [LOAD-EVENT-TYPES] Error loading event types:", error);
      setEventTypesError(`Error: ${error.message}`);
    } finally {
      console.log('🔧 [LOAD-EVENT-TYPES] Finished loading event types');
      setLoadingEventTypes(false);
    }
  };

  // Event Type Selector Component
  const EventTypeSelector = ({ selectedEventType, onSelect, disabled = false }) => {
    if (!availableEventTypes.length) {
      return (
        <div className="eventTypeSelectorEmpty">
          <button 
            className="loadEventTypesButton"
            onClick={handleLoadEventTypes}
            disabled={loadingEventTypes}
          >
            {loadingEventTypes ? (
              <>
                <span className="loadingSpinner"></span>
                Loading Event Types...
              </>
            ) : (
              <>
                📅 Load Calendly Event Types
              </>
            )}
          </button>
          
          {eventTypesError && (
            <div className="eventTypesError">
              ❌ {eventTypesError}
            </div>
          )}
          
          <div className="eventTypesHelp">
            💡 Load your Calendly event types to allow this agent to suggest specific meeting times
          </div>
        </div>
      );
    }

    const handleEventTypeSelect = (eventTypeId) => {
      if (disabled) return;
      
      console.log('🔧 [EVENT-TYPE-SELECT] Event type selected:', {
        eventTypeId,
        eventTypeIdType: typeof eventTypeId,
        eventTypeIdLength: eventTypeId ? eventTypeId.length : 'N/A',
        selectedEventTypeBefore: selectedEventType,
        disabled: disabled
      });
      
      onSelect(eventTypeId);
    };

    return (
      <div className="eventTypesSelector">
        <div className="eventTypesSelectorHeader">
          <span>Select Meeting Type:</span>
          <button 
            className="refreshEventTypesButton"
            onClick={handleLoadEventTypes}
            disabled={loadingEventTypes}
          >
            🔄 Refresh
          </button>
        </div>
        
        <div className="eventTypesList">
          {availableEventTypes.map((eventType) => (
            <div 
              key={eventType.uuid} 
              className={`eventTypeItem ${(selectedEventType === eventType.uuid) ? 'selected' : ''}`}
              onClick={() => handleEventTypeSelect(eventType.uuid)}
            >
              <div className="eventTypeInfo">
                <div className="eventTypeName">{eventType.name}</div>
                <div className="eventTypeDetails">
                  ⏱️ {eventType.duration} min
                  {eventType.description && (
                    <span className="eventTypeDescription">
                      • {eventType.description}
                    </span>
                  )}
                </div>
                <div className="eventTypeId">URI: {eventType.uuid}</div>
              </div>
              <div className="eventTypeToggle">
                {(selectedEventType === eventType.uuid) && (
                  <span className="checkmark">✓</span>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {selectedEventType && (
          <div className="selectedEventTypeInfo">
            ✅ Selected: <strong>{availableEventTypes.find(et => et.uuid === selectedEventType)?.name}</strong>
          </div>
        )}
      </div>
    );
  };

  if (!userId) {
    return <p className="errorMessage">Please login to manage agents.</p>;
  }

  return (
    <>
      {/* Info Container über Agents */}
      <div className={`agentsInfoContainer ${infoCollapsed ? 'collapsed' : ''}`}>
        <div className="agentsInfoHeader" onClick={() => setInfoCollapsed(!infoCollapsed)}>
          <div className="agentsInfoIcon">
            <img src="/img/info.svg" alt="Info" style={{ width: '34px', height: '34px' }} />
          </div>
          <h3>What are AI Agents?</h3>
          <button className="agentsInfoToggle">
            <img 
              src="/img/down.svg" 
              alt="Toggle" 
              className={`toggleIcon ${infoCollapsed ? 'collapsed' : ''}`}
              style={{ width: '16px', height: '16px' }}
            />
          </button>
        </div>
        {!infoCollapsed && (
          <div className="agentsInfoContent">
            <p>
              AI Agents are intelligent assistants that automatically process and respond to incoming emails based on your custom rules. 
              They analyze email content, match it to specific scenarios, and generate appropriate responses or take actions.
            </p>
            <div className="agentsInfoFeatures">
              <div className="agentsInfoFeature">
                <span className="featureIcon">🤖</span>
                <span>Automatic email processing</span>
              </div>
              <div className="agentsInfoFeature">
                <span className="featureIcon">📋</span>
                <span>Custom case-based responses</span>
              </div>
              <div className="agentsInfoFeature">
                <span className="featureIcon">😊</span>
                <span>Happiness-level filtering</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="agentsSettingsContainer">
        <div className="agentsHeader">
          <h3>Agents Management</h3>
          {!showAddAgent && !editingAgent && (
            <button 
              className="addAgentButton"
              onClick={() => {
                setEditingAgent(null);
                setShowAddAgent(true);
              }}
            >
              + Add Agent
            </button>
          )}
        </div>
        
        {agentsLoading ? (
          <div className="agentsLoading">Loading agents...</div>
        ) : (
          <>
            <div className="agentsContainer">
              {agents.length === 0 && !showAddAgent ? (
                <div className="noAgents">
                  <div className="noAgents-content">
                    <div className="noAgents-icon">🤖</div>
                    <h4>Create Your First AI Agent</h4>
                    <p>AI Agents automatically handle incoming emails based on your custom rules and instructions.</p>
                    
                    <div className="agentBenefits">
                      <div className="benefit">
                        <div className="benefitIcon">⚡</div>
                        <div className="benefitText">
                          <strong>Automatic Processing</strong>
                          <span>Agents analyze emails and create appropriate responses</span>
                        </div>
                      </div>
                      <div className="benefit">
                        <div className="benefitIcon">🎯</div>
                        <div className="benefitText">
                          <strong>Smart Categorization</strong>
                          <span>Each agent handles specific types of inquiries</span>
                        </div>
                      </div>
                      <div className="benefit">
                        <div className="benefitIcon">😊</div>
                        <div className="benefitText">
                          <strong>Happiness-Based</strong>
                          <span>Agents work based on customer satisfaction levels</span>
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      className="addAgentButton primary-button"
                      onClick={() => setShowAddAgent(true)}
                    >
                      🚀 Create Your First Agent
                    </button>
                    
                    <div className="agentExample">
                      <div className="exampleTitle">Example Agent:</div>
                      <div className="exampleContent">
                        <div className="exampleField">
                          <strong>Name:</strong> Customer Support Agent
                        </div>
                        <div className="exampleField">
                          <strong>Case:</strong> Customer complaints about service quality
                        </div>
                        <div className="exampleField">
                          <strong>Task:</strong> Apologize sincerely and offer a solution or compensation
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {agents.length > 0 && (
                    <div className="agentsList">
                      {agents.map((agent) => (
                        <div key={agent.id} className="agentItem">
                          <div className="agentInfo">
                            <div className="agentHeader">
                              <div className="agentName">{agent.name}</div>
                              <div className="agentCaseNumber">
                                Case {agent.caseNumber || 'N/A'}
                              </div>
                            </div>
                            <div className="agentCase">{agent.case}</div>
                            {agent.type === 'Reply' && agent.task && (
                              <div className="agentTask">{agent.task}</div>
                            )}
                            {agent.type === 'No Reply' && (
                              <div className="agentNoReplyNote">
                                <span className="noReplyIcon">🚫</span>
                                <span>No automatic reply will be generated for this case</span>
                              </div>
                            )}
                            <div className="agentMetadata">
                              <div className="agentType">
                                <span className="agentTypeLabel">Type:</span>
                                <span className={`agentTypeBadge ${agent.type === 'No Reply' ? 'no-reply' : 'reply'}`}>
                                  {agent.type}
                                </span>
                              </div>
                              <div className="agentHappiness">
                                <span className="agentHappinessLabel">😊 Happiness:</span>
                                <span className="agentHappinessRange">
                                  {agent.happinessMin || 1} - {agent.happinessMax || 10}
                                </span>
                              </div>
                              {agent.useCalendly && (
                                <div className="agentCalendly">
                                  <span className="agentCalendlyLabel">📅 Calendly:</span>
                                  <span className="agentCalendlyBadge enabled">
                                    Enabled
                                  </span>
                                  {agent.selectedEventType && availableEventTypes.length > 0 && (
                                    <div className="agentEventType">
                                      <span className="agentEventTypeLabel">Meeting Type:</span>
                                      <span className="agentEventTypeBadge">
                                        {availableEventTypes.find(et => et.uuid === agent.selectedEventType)?.name || `ID: ${agent.selectedEventType}`}
                                      </span>
                                    </div>
                                  )}
                                  {agent.calendlyGuidelines && agent.calendlyGuidelines.trim() && (
                                    <div className="agentCalendlyGuidelines">
                                      <span className="agentCalendlyGuidelinesLabel">Guidelines:</span>
                                      <span className="agentCalendlyGuidelinesText">
                                        {agent.calendlyGuidelines.length > 100 
                                          ? `${agent.calendlyGuidelines.substring(0, 100)}...` 
                                          : agent.calendlyGuidelines}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="agentActions">
                            <button 
                              className="editAgentButton"
                              onClick={() => handleEditAgent(agent)}
                              title="Edit Agent"
                            >
                              <img src="/img/edit.svg" alt="Edit" className="actionIcon" />
                            </button>
                            <button 
                              className="deleteAgentButton"
                              onClick={() => handleDeleteAgent(agent)}
                              title="Delete Agent"
                            >
                              <img src="/img/delete.svg" alt="Delete" className="actionIcon" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {agentsMessage && (
              <div className="agentsMessage">{agentsMessage}</div>
            )}
          </>
        )}
        
        {/* Edit Agent Modal */}
        {editingAgent && (
          <div className="agentsSettingsModal">
            <div className="agentsSettingsModalOverlay" onClick={handleCancelEdit}></div>
            <div className="agentsSettingsModalContent">
              <div className="agentForm editForm">
                <div className="agentFormHeader">
                  <div className="formIcon">✏️</div>
                  <h4>Edit Agent (Case {editingAgent.caseNumber || 'N/A'})</h4>
                  <button className="agentsSettingsModalCloseButton" onClick={handleCancelEdit}>×</button>
                </div>
                
                <div className="agentsSettingsModalFormBody">
                  <div className="settingField">
                    <HappinessRangeSlider
                      minValue={editingAgent.happinessMin}
                      maxValue={editingAgent.happinessMax}
                      onMinChange={(value) => setEditingAgent(prev => ({ ...prev, happinessMin: value }))}
                      onMaxChange={(value) => setEditingAgent(prev => ({ ...prev, happinessMax: value }))}
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Name:</label>
                    <input
                      type="text"
                      value={editingAgent.name}
                      onChange={(e) => setEditingAgent(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter agent's name"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Case Description:</label>
                    <textarea
                      value={editingAgent.case}
                      onChange={(e) => setEditingAgent(prev => ({ ...prev, case: e.target.value }))}
                      placeholder="Describe when this agent should be used"
                      rows="3"
                    />
                    <small className="fieldHint">
                      Describe the type of emails this agent should handle. The AI will match incoming emails to this case.
                    </small>
                  </div>
                  
                  {editingAgent.type === 'Reply' && (
                    <div className="settingField">
                      <label>Task Instructions:</label>
                      <textarea
                        value={editingAgent.task}
                        onChange={(e) => setEditingAgent(prev => ({ ...prev, task: e.target.value }))}
                        placeholder="Describe what the agent should do"
                        rows="4"
                      />
                      <small className="fieldHint">
                        Detailed instructions for how the agent should respond to emails matching this case.
                      </small>
                    </div>
                  )}
                  
                  <div className="settingField">
                    <label>Type:</label>
                    <select
                      value={editingAgent.type}
                      onChange={(e) => setEditingAgent(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="Reply">Reply</option>
                      <option value="No Reply">No Reply</option>
                    </select>
                  </div>
                  
                  <div className="settingField">
                    <div className="calendlyToggleContainer">
                      <div className="calendlyToggleHeader">
                        <div className="calendlyToggleInfo">
                          <div className="calendlyToggleLabel">📅 Calendly Integration</div>
                          <div className="calendlyToggleDescription">
                            Allow this agent to access and suggest available appointment times
                          </div>
                        </div>
                        <div 
                          className={`calendlyToggle ${editingAgent.useCalendly ? 'active' : ''}`} 
                          onClick={() => setEditingAgent(prev => ({ ...prev, useCalendly: !prev.useCalendly }))}
                        >
                          <div className="calendlyToggleSwitch"></div>
                        </div>
                      </div>
                      {editingAgent.useCalendly && (
                        <div className="calendlyToggleNote">
                          ✅ This agent will have access to your Calendly availability and can suggest specific appointment times
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {editingAgent.useCalendly && (
                    <div className="settingField">
                      <label>Meeting Type for this Agent:</label>
                      <EventTypeSelector
                        selectedEventType={editingAgent.selectedEventType}
                        onSelect={(eventTypeId) => {
                          console.log('🔧 [EDIT-FORM] Setting selectedEventType for editingAgent:', {
                            eventTypeId,
                            eventTypeIdType: typeof eventTypeId,
                            currentSelectedEventType: editingAgent.selectedEventType
                          });
                          setEditingAgent(prev => ({ ...prev, selectedEventType: eventTypeId }));
                        }}
                      />
                    </div>
                  )}
                  
                  {editingAgent.useCalendly && (
                    <div className="settingField">
                      <label>Calendly Guidelines:</label>
                      <textarea
                        value={editingAgent.calendlyGuidelines}
                        onChange={(e) => setEditingAgent(prev => ({ ...prev, calendlyGuidelines: e.target.value }))}
                        placeholder="Enter specific instructions for how this agent should handle Calendly appointments..."
                        rows="4"
                      />
                      <small className="fieldHint">
                        Spezifische Anweisungen für den Umgang mit Calendly-Terminen (z.B. "Schlage 3 Termine vor, wähle sie aus verschiedenen Tagen, bevorzuge Vormittagstermine")
                      </small>
                    </div>
                  )}
                </div>
                
                <div className="agentsSettingsModalFormActions">
                  <div className="agentFormActions">
                    <button 
                      className="cancelAgentButton"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                    <button 
                      className="saveAgentButton"
                      onClick={handleUpdateAgent}
                    >
                      Update Agent
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Agent Modal */}
        {showAddAgent && (
          <div className="agentsSettingsModal">
            <div className="agentsSettingsModalOverlay" onClick={handleCancelAdd}></div>
            <div className="agentsSettingsModalContent">
              <div className="agentForm addForm">
                <div className="agentFormHeader">
                  <div className="formIcon"><img src="/img/agent.svg" alt="Add" style={{ width: '34px', height: '34px', filter: 'invert(1)' }} /></div>
                  <h4>Add New Agent (Case {agents.length + 1})</h4>
                  <button className="agentsSettingsModalCloseButton" onClick={handleCancelAdd}>×</button>
                </div>
                
                <div className="agentsSettingsModalFormBody">
                  <div className="settingField">
                    <HappinessRangeSlider
                      minValue={newAgent.happinessMin}
                      maxValue={newAgent.happinessMax}
                      onMinChange={(value) => setNewAgent(prev => ({ ...prev, happinessMin: value }))}
                      onMaxChange={(value) => setNewAgent(prev => ({ ...prev, happinessMax: value }))}
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Name:</label>
                    <input
                      type="text"
                      value={newAgent.name}
                      onChange={(e) => setNewAgent(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter agent's name"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Case Description:</label>
                    <textarea
                      value={newAgent.case}
                      onChange={(e) => setNewAgent(prev => ({ ...prev, case: e.target.value }))}
                      placeholder="Describe when this agent should be used (e.g., 'Customer complaints about product quality')"
                      rows="3"
                    />
                    <small className="fieldHint">
                      Describe the type of emails this agent should handle. The AI will match incoming emails to this case.
                    </small>
                  </div>
                  
                  {newAgent.type === 'Reply' && (
                    <div className="settingField">
                      <label>Task Instructions:</label>
                      <textarea
                        value={newAgent.task}
                        onChange={(e) => setNewAgent(prev => ({ ...prev, task: e.target.value }))}
                        placeholder="Describe what the agent should do when handling emails (e.g., 'Apologize for the issue and offer a solution')"
                        rows="4"
                      />
                      <small className="fieldHint">
                        Detailed instructions for how the agent should respond to emails matching this case.
                      </small>
                    </div>
                  )}
                  
                  <div className="settingField">
                    <label>Type:</label>
                    <select
                      value={newAgent.type}
                      onChange={(e) => setNewAgent(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="Reply">Reply</option>
                      <option value="No Reply">No Reply</option>
                    </select>
                  </div>
                  
                  <div className="settingField">
                    <div className="calendlyToggleContainer">
                      <div className="calendlyToggleHeader">
                        <div className="calendlyToggleInfo">
                          <div className="calendlyToggleLabel">📅 Calendly Integration</div>
                          <div className="calendlyToggleDescription">
                            Allow this agent to access and suggest available appointment times
                          </div>
                        </div>
                        <div 
                          className={`calendlyToggle ${newAgent.useCalendly ? 'active' : ''}`} 
                          onClick={() => setNewAgent(prev => ({ ...prev, useCalendly: !prev.useCalendly }))}
                        >
                          <div className="calendlyToggleSwitch"></div>
                        </div>
                      </div>
                      {newAgent.useCalendly && (
                        <div className="calendlyToggleNote">
                          ✅ This agent will have access to your Calendly availability and can suggest specific appointment times
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {newAgent.useCalendly && (
                    <div className="settingField">
                      <label>Meeting Type for this Agent:</label>
                      <EventTypeSelector
                        selectedEventType={newAgent.selectedEventType}
                        onSelect={(eventTypeId) => {
                          console.log('🔧 [ADD-FORM] Setting selectedEventType for newAgent:', {
                            eventTypeId,
                            eventTypeIdType: typeof eventTypeId,
                            currentSelectedEventType: newAgent.selectedEventType
                          });
                          setNewAgent(prev => ({ ...prev, selectedEventType: eventTypeId }));
                        }}
                      />
                    </div>
                  )}
                  
                  {newAgent.useCalendly && (
                    <div className="settingField">
                      <label>Calendly Guidelines:</label>
                      <textarea
                        value={newAgent.calendlyGuidelines}
                        onChange={(e) => setNewAgent(prev => ({ ...prev, calendlyGuidelines: e.target.value }))}
                        placeholder="Enter specific instructions for how this agent should handle Calendly appointments..."
                        rows="4"
                      />
                      <small className="fieldHint">
                        Spezifische Anweisungen für den Umgang mit Calendly-Terminen (z.B. "Schlage 3 Termine vor, wähle sie aus verschiedenen Tagen, bevorzuge Vormittagstermine")
                      </small>
                    </div>
                  )}
                </div>
                
                <div className="agentsSettingsModalFormActions">
                  <div className="agentFormActions">
                    <button 
                      className="cancelAgentButton"
                      onClick={handleCancelAdd}
                    >
                      Cancel
                    </button>
                    <button 
                      className="saveAgentButton"
                      onClick={handleAddAgent}
                    >
                      Add Agent
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AgentsSettings; 